import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import SpecificSpaceTable from './SpecificSpaceTable';
import DisableSpaceModal from './DisableSpaceModal';
import AddUsersModal from './AddUsersModal';
import { CompanySpace } from '../../util/Types/CompanySpace';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import { getAPIErrorMessage } from '../../helpers/helpers';
import { CompanySpacesService } from '../../service/CompanySpacesService';
import {
  ArrowLeft,
  Copy,
  Eye,
  EyeSlash,
  PencilSimpleLine,
} from '@phosphor-icons/react';
import LoadingSpinner from '../UI/LoadingSpinner';
import SetFluxTokenLimitModal from './SetFluxTokenLimitModal';
import UsersAddedModal from './UsersAddedModal';
import { useAuthStore } from '../../store/storeAuth';
import IconButtonTooltip from '../UI/IconButtonTooltip/IconButtonTooltip';
import EditSpaceModal from './EditSpaceModal';
import RefreshSpaceApiKeyModal from './RefreshSpaceApiKeyModal';

interface Props {
  spaceId: string;
  setSelectedSpaceId: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const statsTime = {
  'This Month': 'This Month',
  '30 Days': '30 Days',
  '90 Days': '90 Days',
  Lifetime: 'Lifetime',
};

const queryTime: { [key: string]: string } = {
  'This Month': 'this_month',
  '30 Days': '30_days',
  '90 Days': '90_days',
};

const boxStyles = {
  display: 'flex',
  padding: '12px',
  width: '80%',
  height: '60px',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: '4px',
  borderRadius: '8px',
  color: '#fff',
};

const SpaceDetails: React.FC<Props> = ({ spaceId, setSelectedSpaceId }) => {
  const authUser = useAuthStore(state => state.user);

  const setNotification = useNotificationStore(state => state.setNotification);
  const [selectedStatsTime, setSelectedStatsTime] = useState(
    statsTime['Lifetime'],
  );
  const [showDisableSpaceModal, setShowDisableSpaceModal] = useState(false);
  const [showAddUsersModal, setShowAddUsersModal] = useState(false);
  const [spaceDataLoading, setSpaceDataLoading] = useState(true);
  const [companySpaceData, setCompanySpaceData] = useState<
    undefined | CompanySpace
  >();
  const [showSetFluxTokenLimitModal, setShowSetFluxTokenLimitModal] =
    useState(false);
  const [showUsersAddessModal, setShowUsersAddedModal] = useState(false);
  const [openModalName, setOpenModalName] = useState(false);
  const [showRefreshSpaceApiKeyModal, setShowRefreshSpaceApiKeyModal] =
    useState(false);
  const [showSpaceApiKey, setShowSpaceApiKey] = useState(false);

  const dataBox = companySpaceData
    ? [
        {
          background: 'linear-gradient(90deg, #1436FF 0%, #396AFC 100%)',
          label: 'Total FluxTokens',
          value: companySpaceData?.usedTokens?.toFixed(2),
        },
        {
          background:
            'linear-gradient(53deg, #FD746C 16.71%, #FE846A 48.74%, #FF9068 80.78%)',
          label: 'Other',
          value: companySpaceData?.apiCallsTokens?.toFixed(2),
        },
        {
          background: 'linear-gradient(90deg, #142BFF 0%, #396AFC 100%)',
          label: 'Text FluxTokens',
          value: companySpaceData?.textTokens?.toFixed(2),
        },
        {
          background: 'linear-gradient(59deg, #683BB8 7.2%, #A969FD 92.35%)',
          label: 'Image FluxTokens',
          value: companySpaceData?.imageTokens?.toFixed(2),
        },
        {
          background: 'linear-gradient(63deg, #73E49A 14.58%, #3FC36D 82.62%)',
          label: 'Audio FluxTokens',
          value: companySpaceData?.audioTokens?.toFixed(2),
        },
        {
          background: 'linear-gradient(90deg, #1436FF 0%, #396AFC 100%)',
          label: 'Agents Created',
          value: companySpaceData?.numberOfFlows,
        },
        {
          background: 'linear-gradient(90deg, #1436FF 0%, #396AFC 100%)',
          label: 'Node Runs',
          value: companySpaceData?.nodeRuns,
        },
      ]
    : [];

  const copySpaceId = async () => {
    try {
      navigator.clipboard.writeText(spaceId);
      setNotification({
        type: NotificationType.Success,
        message: 'Space ID copied successfully',
      });
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: 'Something went wrong',
      });
    }
  };

  const copySpaceApiKey = async () => {
    if (!companySpaceData?.apiKey) return;
    try {
      await navigator.clipboard.writeText(companySpaceData.apiKey);
      setNotification({
        type: NotificationType.Success,
        message: 'API key copied to clipboard',
      });
    } catch {
      setNotification({
        type: NotificationType.Error,
        message: 'Failed to copy API key',
      });
    }
  };

  const getCompanySpace = useCallback(async () => {
    if (!authUser?.selectedCompanyId)
      return setNotification({
        type: NotificationType.Error,
        message: 'Switch to company account',
      });

    try {
      const query =
        selectedStatsTime === statsTime.Lifetime
          ? ''
          : `&date=${queryTime[selectedStatsTime]}`;
      const { data } = await CompanySpacesService.getCompanySpace(
        authUser.selectedCompanyId,
        spaceId,
        query,
      );
      setCompanySpaceData(data);
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error as string),
      });
    }

    setSpaceDataLoading(false);
  }, [authUser, spaceId, selectedStatsTime]);

  const onSaveSpaceName = async (name: string) => {
    try {
      if (!authUser?.selectedCompanyId) {
        setNotification({
          type: NotificationType.Error,
          message: 'Company ID is required',
        });
        return;
      }

      await CompanySpacesService.updateCompanySpace(
        authUser.selectedCompanyId,
        {
          spaceId,
          name,
        },
      );

      getCompanySpace();
      setNotification({
        type: NotificationType.Success,
        message: 'Space name updated successfully',
      });
    } catch (error) {
      console.error('SpaceDetails: Error updating space name:', error);
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error as string),
      });
    }
  };

  useEffect(() => {
    getCompanySpace();
  }, [getCompanySpace]);

  const onOpenOrClose = (open: boolean) => {
    setOpenModalName(open);
  };

  if (spaceDataLoading) return <LoadingSpinner />;

  return (
    <>
      {showDisableSpaceModal && (
        <DisableSpaceModal
          onClose={() => setShowDisableSpaceModal(false)}
          spaceId={spaceId}
        />
      )}

      {showAddUsersModal && (
        <AddUsersModal
          onClose={() => setShowAddUsersModal(false)}
          spaceId={spaceId}
          getCompanySpace={getCompanySpace}
          setShowUsersAddedModal={setShowUsersAddedModal}
        />
      )}

      {showUsersAddessModal && (
        <UsersAddedModal onClose={() => setShowUsersAddedModal(false)} />
      )}

      {showSetFluxTokenLimitModal && (
        <SetFluxTokenLimitModal
          onClose={() => setShowSetFluxTokenLimitModal(false)}
          spaceId={spaceId}
          getCompanySpace={getCompanySpace}
        />
      )}

      {showRefreshSpaceApiKeyModal && (
        <RefreshSpaceApiKeyModal
          onClose={() => setShowRefreshSpaceApiKeyModal(false)}
          spaceId={spaceId}
          onSuccess={() => {
            setShowSpaceApiKey(true);
            getCompanySpace();
          }}
        />
      )}

      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10x',
            width: '100%',
          }}
        >
          <Box
            sx={{
              borderBottom: '1px solid rgba(234, 236, 240, 1)',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '10x',
              height: '64px',
            }}
          >
            <Box
              sx={{
                cursor: 'pointer',
                marginLeft: '10px',
                display: 'inline-block',
              }}
              onClick={() => setSelectedSpaceId(undefined)}
            >
              <ArrowLeft color="rgba(52, 51, 48, 1)" size={18} />
            </Box>
            <Typography
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                color: '#000',
                fontFamily: 'Roboto',
                fontSize: '20px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '32px',
                margin: '24px',
                pb: '10px',
              }}
            >
              Space Details
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 0',
            borderBottom: '1px solid rgba(234, 236, 240, 1)',
            paddingX: '24px',
            paddingY: '15px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <Typography
                sx={{
                  color: 'rgba(71, 84, 103, 1)',
                  fontSize: '24px',
                  lineHeight: '32px',
                }}
              >
                {companySpaceData?.name}
              </Typography>

              <IconButtonTooltip
                onClick={() => onOpenOrClose(true)}
                title="Edit space name"
              >
                <PencilSimpleLine size={16} color={'#667085'} />
              </IconButtonTooltip>
            </Box>
            <Typography
              sx={{
                fontSize: '14px',
                lineHeight: '22px',
                letterSpacing: '0.1px',
                color: 'rgba(71, 84, 103, 1)',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              {companySpaceData?.email}
              <Tooltip
                arrow
                placement="top"
                title="Copy space ID"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -14],
                        },
                      },
                    ],
                  },
                }}
              >
                <IconButton onClick={copySpaceId}>
                  <Copy size={16} />
                </IconButton>
              </Tooltip>
            </Typography>

            {companySpaceData?.apiKey && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginTop: '8px',
                  marginBottom: '8px',
                }}
              >
                <TextField
                  value={companySpaceData.apiKey}
                  type={showSpaceApiKey ? 'text' : 'password'}
                  size="small"
                  label="Space API Key"
                  sx={{ minWidth: '320px' }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Box
                          sx={{ cursor: 'pointer', display: 'flex' }}
                          onClick={() => setShowSpaceApiKey(!showSpaceApiKey)}
                        >
                          {showSpaceApiKey ? <EyeSlash /> : <Eye />}
                        </Box>
                        <Box
                          sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            marginLeft: 1,
                          }}
                          onClick={copySpaceApiKey}
                        >
                          <Copy />
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    border: '1px solid rgba(240, 84, 80, 1)',
                    color: 'rgba(240, 84, 80, 1)',
                    '&:hover': {
                      border: '1px solid rgba(240, 84, 80, 1)',
                      color: 'rgba(240, 84, 80, 1)',
                      background: 'transparent',
                    },
                  }}
                  onClick={() => setShowRefreshSpaceApiKeyModal(true)}
                >
                  Refresh
                </Button>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0.1px',
                  color: 'rgba(71, 84, 103, 1)',
                }}
              >
                {companySpaceData?.users.length} users
              </Typography>
              {companySpaceData?.limit ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Typography sx={{ color: 'rgba(54, 80, 222, 1)' }}>
                    {companySpaceData.limit} FluxToken
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(54, 80, 222, 1)',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                    onClick={() => setShowSetFluxTokenLimitModal(true)}
                  >
                    Edit
                  </Typography>
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: 'rgba(54, 80, 222, 1)',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                  onClick={() => setShowSetFluxTokenLimitModal(true)}
                >
                  Set FluxToken Limit
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <Button
              variant="text"
              onClick={() => setShowDisableSpaceModal(true)}
            >
              Disable Space
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowAddUsersModal(true)}
            >
              Add Users
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingX: '24px',
            paddingY: '15px',
          }}
        >
          <Typography
            sx={{
              color: 'rgba(71, 84, 103, 1)',
              fontSize: '18px',
              fontWeight: 500,
              lineHeight: '28.8px',
              letterspacing: '0.15px',
            }}
          >
            {companySpaceData?.name} Stats Overview
          </Typography>

          <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {Object.values(statsTime).map(statsTime => (
              <Box
                key={statsTime}
                onClick={() => setSelectedStatsTime(statsTime)}
                sx={{
                  borderRadius: '8px',
                  padding: '3px 8px',
                  background:
                    selectedStatsTime === statsTime
                      ? 'rgba(54, 80, 222, 1)'
                      : 'rgba(234, 236, 240, 1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <Typography
                  sx={{
                    color:
                      selectedStatsTime === statsTime
                        ? 'white'
                        : 'rgba(16, 24, 40, 1)',
                    fontSize: '12px',
                    lineHeight: '19.92px',
                    letterSpacing: '0.4px',
                  }}
                >
                  {statsTime}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ paddingX: '24px' }}>
          {dataBox.map((item, index) => (
            <Grid item xs={12} sm={3} md={1.7} key={index}>
              <Box sx={{ ...boxStyles, backgroundImage: item.background }}>
                <Typography variant="h5">{item.value}</Typography>
                <Typography variant="body2">{item.label}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {openModalName && (
          <EditSpaceModal
            spaceName={companySpaceData?.name!}
            onClose={() => onOpenOrClose(false)}
            onSave={onSaveSpaceName}
          />
        )}

        {companySpaceData?.users && (
          <SpecificSpaceTable
            spaceId={spaceId}
            users={companySpaceData.users}
            getCompanySpace={getCompanySpace}
          />
        )}
      </Box>
    </>
  );
};

export default SpaceDetails;
